import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Select, type SelectProps } from 'antd';
import styled from 'styled-components';

import { LARGE_FIELD } from './constants';
import { Container, ErrorText, Label, RequiredStar } from './form-shared';

const largeSelectStyles = `
  font-size: ${LARGE_FIELD.fontSize}px;
  height: ${LARGE_FIELD.height}px;

  .ant-select-selector {
    height: ${LARGE_FIELD.height}px !important;
    font-size: ${LARGE_FIELD.fontSize}px !important;
    align-items: center;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    line-height: ${LARGE_FIELD.height}px !important;
    font-size: ${LARGE_FIELD.fontSize}px;
  }
`;

const largeDropdownStyles = { fontSize: LARGE_FIELD.fontSize };

const largeOptionStyles: React.CSSProperties = {
  fontSize: LARGE_FIELD.fontSize,
  minHeight: LARGE_FIELD.height,
  display: 'flex',
  alignItems: 'center'
};

interface StyledSelectProps {
  $fullWidth?: boolean;
  $large?: boolean;
}

const StyledSelect = styled(Select)<StyledSelectProps>`
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
  ${({ $large }) => $large && largeSelectStyles}
`;

interface FormSelectFieldProps extends Omit<SelectProps, 'size'> {
  label?: string;
  style?: React.CSSProperties;
  name: string;
  size?: 'large' | 'middle' | 'small';
  fullWidth?: boolean;
  required?: boolean;
}

function FormSelectField({
  label,
  style,
  name,
  size = 'large',
  fullWidth,
  required,
  options,
  ...rest
}: FormSelectFieldProps) {
  const ctx = useFormContext();
  const control = ctx?.control ?? undefined;
  const isLarge = size === 'large';

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <Container style={style} $fullWidth={fullWidth}>
          {label && (
            <Label>
              {label}
              {required && <RequiredStar>*</RequiredStar>}
            </Label>
          )}
          <StyledSelect
            {...field}
            $large={isLarge}
            $fullWidth={fullWidth}
            options={options}
            styles={
              isLarge ? { popup: { root: largeDropdownStyles } } : undefined
            }
            optionRender={
              isLarge
                ? (option) => (
                    <div style={largeOptionStyles}>{option.label}</div>
                  )
                : undefined
            }
            {...rest}
          />
          {error && <ErrorText>{error?.message}</ErrorText>}
        </Container>
      )}
    />
  );
}

export default FormSelectField;
